import { useEffect } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';

/**
 * FullscreenControl — a reusable Leaflet control that toggles the map's own
 * container in and out of browser full screen. Drop it in as a child of any
 * react-leaflet <MapContainer/> and it renders a button styled to match the
 * native zoom control (top-left by default, under the +/− control):
 *
 *   <MapContainer>
 *     <TileLayer ... />
 *     <FullscreenControl />
 *     ...
 *   </MapContainer>
 *
 * We fullscreen `map.getContainer()` (the .leaflet-container element itself),
 * so no wrapper refactor is needed — the browser promotes it to the top layer
 * at viewport size. On every fullscreenchange (which also covers Esc-to-exit)
 * we swap the icon and call invalidateSize() so Leaflet re-renders its tiles
 * at the new dimensions.
 */

// lucide "maximize" / "minimize" glyphs, inlined so the control needs no React
// inside the Leaflet DOM. currentColor picks up the control's dark link color.
const ICON_ENTER = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3"/><path d="M21 8V5a2 2 0 0 0-2-2h-3"/><path d="M3 16v3a2 2 0 0 0 2 2h3"/><path d="M16 21h3a2 2 0 0 0 2-2v-3"/></svg>`;
const ICON_EXIT = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3v3a2 2 0 0 1-2 2H3"/><path d="M21 8h-3a2 2 0 0 1-2-2V3"/><path d="M3 16h3a2 2 0 0 1 2 2v3"/><path d="M16 21v-3a2 2 0 0 1 2-2h3"/></svg>`;

interface FullscreenControlProps {
  /** Leaflet control corner. Defaults to 'topleft' (below the zoom control). */
  position?: L.ControlPosition;
}

export function FullscreenControl({ position = 'topleft' }: FullscreenControlProps): null {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();

    const sync = (btn: HTMLAnchorElement) => {
      const isFs = document.fullscreenElement === container;
      btn.innerHTML = isFs ? ICON_EXIT : ICON_ENTER;
      const label = isFs ? 'Exit full screen' : 'View full screen';
      btn.title = label;
      btn.setAttribute('aria-label', label);
    };

    const control = new L.Control({ position });
    let button: HTMLAnchorElement | null = null;

    control.onAdd = () => {
      const bar = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
      const btn = L.DomUtil.create('a', '', bar) as HTMLAnchorElement;
      btn.href = '#';
      btn.setAttribute('role', 'button');
      // Center the SVG regardless of leaflet's default block sizing.
      btn.style.display = 'flex';
      btn.style.alignItems = 'center';
      btn.style.justifyContent = 'center';
      // Stop clicks from panning/zooming the map or following the href.
      L.DomEvent.disableClickPropagation(bar);
      L.DomEvent.on(btn, 'click', L.DomEvent.stop);
      L.DomEvent.on(btn, 'click', () => {
        if (document.fullscreenElement === container) {
          document.exitFullscreen?.();
        } else {
          container.requestFullscreen?.();
        }
      });
      button = btn;
      sync(btn);
      return bar;
    };

    control.addTo(map);

    const onChange = () => {
      if (button) sync(button);
      // Wait a frame so we read the post-resize dimensions before Leaflet
      // recomputes its size and reloads tiles.
      requestAnimationFrame(() => map.invalidateSize());
    };
    document.addEventListener('fullscreenchange', onChange);

    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      // Exit fullscreen if this map owned it, so unmount doesn't strand the UI.
      if (document.fullscreenElement === container) document.exitFullscreen?.();
      control.remove();
    };
  }, [map, position]);

  return null;
}

export default FullscreenControl;
